import { useState } from "react";
import { message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconLinePlus /*, IconLineEdit, IconLineLink */ } from "@kalodata/kds-design-icons";
import { CreateCharacterEntityModal } from "@pages/library/CreateCharacterEntityModal";
import type { CreateCharacterMode } from "@pages/library/CreateCharacterEntityModal/types";
import { CharacterAssetCreateFormRequestCategory } from "@apis/generated/openapi";
import type { CharacterType } from "@pages/library/librarySlice";
// import { CreateEntityMethodDropdown } from "./CreateEntityMethodDropdown";
// import type { ClipDropdownOption } from "@components/ClipDropdown/ClipDropdown";
import { useAuthCreateCharacters } from "../hooks/useAuthCreateCharacters";

// const ICON_CLASS = "shrink-0 text-kds-fg-soft";

// Video Link option is disabled for this release — only manual creation is supported
// const OPTIONS: ClipDropdownOption[] = [
//   {
//     key: "videoLink",
//     label: (
//       <div className="flex items-center gap-[8px]">
//         <IconLineLink size={16} className={ICON_CLASS} />
//         <Trans>Video Link</Trans>
//       </div>
//     ),
//   },
//   {
//     key: "manual",
//     label: (
//       <div className="flex items-center gap-[8px]">
//         <IconLineEdit size={16} className={ICON_CLASS} />
//         <Trans>Create Manually</Trans>
//       </div>
//     ),
//   },
//   // {
//   //   key: "library",
//   //   label: (
//   //     <div className="flex items-center gap-[8px]">
//   //       <IconLineFolder size={16} className={ICON_CLASS} />
//   //       <Trans>Add From Library</Trans>
//   //     </div>
//   //   ),
//   // },
// ];

const characterTypeToCategory: Record<CharacterType, CharacterAssetCreateFormRequestCategory> = {
  model: CharacterAssetCreateFormRequestCategory.MODEL,
  animal: CharacterAssetCreateFormRequestCategory.ANIMAL,
};

export interface CreateCharacterEntityCardProps {
  characterType: CharacterType;
  /** 当前 character 列表已创建数量，用于免费版额度预检 */
  existingCount: number;
  /** 创建成功后刷新列表（由 UICardWrap 注入） */
  onCreated?: () => void;
}

/**
 * Library > Characters > Models/Animals 占位卡片。
 *
 * 点击卡片内容区（+ 图标 + 文字）弹出 Creation Method 下拉，挑选 videoLink / manual / library 后再以
 * 对应模式打开 CreateCharacterEntityModal；免费用户已达 1 个角色上限时弹升级引导。
 *
 * 卡片外层负责样式（边框/背景/尺寸/hover），内层内容区作为 dropdown trigger，
 * 使 dropdown 锚定在内容区下方，与卡片底部重叠展示（匹配 Figma 设计）。
 */
export const CreateCharacterEntityCard = ({
  characterType,
  existingCount,
  onCreated,
}: CreateCharacterEntityCardProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultMode, setDefaultMode] = useState<CreateCharacterMode>("manual");
  const { canCreateCharacter } = useAuthCreateCharacters(existingCount);
  const handleClick = () => {
    if (!canCreateCharacter) {
      message.warning(t("Your membership tier only allows 1 character."));
      return;
    }
    setDefaultMode("manual");
    setModalOpen(true);
  };

  return (
    <>
      <div
        className="group bg-kds-bg border border-kds-border rounded-[8px] h-[327px] w-full flex items-center justify-center cursor-pointer hover:bg-kds-bg-element transition-colors hover:border-kds-accent"
        onClick={handleClick}
      >
        {/* <CreateEntityMethodDropdown options={OPTIONS} onChange={handlePick}> */}
        <div className="flex flex-col items-center gap-[8px]">
          <div className="bg-kds-bg-subtle rounded-full size-[48px] flex items-center justify-center transition-colors">
            <IconLinePlus size={20} className="text-kds-fg" />
          </div>
          <span className="text-[14px] text-kds-fg">
            <Trans>Create Character</Trans>
          </span>
        </div>
        {/* </CreateEntityMethodDropdown> */}
      </div>

      <CreateCharacterEntityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={onCreated}
        defaultMode={defaultMode}
        defaultCategory={characterTypeToCategory[characterType]}
      />
    </>
  );
};
